import React, { useState } from "react";
import { Info } from "lucide-react";
import { DebtScheduleRow } from "../../lib/finance/cashflow";
import { formatScaledCurrency, Scale } from "../../lib/pl/format";
import {
  Tooltip as RadixTooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { cn } from "../../lib/utils";
import { ICurrency } from "@/types";

interface DebtScheduleTableProps {
  rows: DebtScheduleRow[];
  currency: ICurrency;
  scale: Scale;
  exitYear: number | null;
  totalLoanAmount: number;
  totalInterest: number;
  totalPrincipalRepaid: number;
}

export default function DebtScheduleTable({
  rows,
  currency,
  scale,
  exitYear,
  totalLoanAmount,
  totalInterest,
  totalPrincipalRepaid,
}: DebtScheduleTableProps) {
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);

  const getRowStyle = (row: DebtScheduleRow) => {
    const baseClasses =
      "border-b border-slate-100 hover:bg-slate-50/50 transition-colors";

    if (row.id === "debt-eop") {
      return cn(
        baseClasses,
        "bg-slate-100 font-bold border-l-4 border-blue-500"
      );
    }

    if (
      row.id === "interest" ||
      row.id === "repayment" ||
      row.id === "balloon-repayment"
    ) {
      return cn(baseClasses, "bg-red-50/20");
    }

    return baseClasses;
  };

  const renderValue = (value: number) => {
    const formatted = formatScaledCurrency(Math.abs(value), currency, scale);
    if (value < 0) {
      return <span className="text-red-600">({formatted})</span>;
    }
    return formatted;
  };

  const getTooltipContent = (rowId: string) => {
    const tooltips: Record<string, string> = {
      "debt-bop": "Debt balance at the beginning of the period",
      "debt-drawdown": "New debt drawn during the period (only in Year 0)",
      interest: "Interest expense on outstanding debt",
      repayment: "Regular principal repayment (amortization)",
      "balloon-repayment":
        "Balloon repayment at exit - pays all remaining debt",
      "debt-eop": "Debt balance at the end of the period after repayments",
    };

    return tooltips[rowId] || "Debt schedule calculation";
  };

  // Преобразуем массив years в 11 лет (Year 0 - Year 10)
  const getYearData = (row: DebtScheduleRow, yearIndex: number) => {
    const yearData = row.years.find((y) => y.year === yearIndex);
    return yearData ? yearData.value : 0;
  };

  const yearsToShow = 11; // Year 0 через Year 10

  return (
    <div className="w-full mx-auto mt-8">
      <div className="rounded-xl border border-slate-200 bg-white shadow-card overflow-hidden">
        <div className="p-4 border-b border-slate-200 bg-indigo-50">
          <h3 className="text-lg font-bold text-slate-900">Debt Schedule</h3>
          <p className="text-sm text-slate-600 mt-1">
            Detailed breakdown of debt financing including drawdown, interest,
            and repayment schedule
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 sticky top-0 z-20">
              <tr>
                <th className="text-left px-4 py-3 font-semibold text-slate-700 border-r border-slate-200 min-w-[220px]">
                  Debt Schedule Item
                </th>
                {Array.from({ length: yearsToShow }, (_, i) => i).map(
                  (year) => (
                    <th
                      key={year}
                      className="px-4 py-3 text-center font-bold text-slate-700 border-r border-slate-200 bg-slate-100 min-w-[100px]"
                    >
                      {year === 0 ? "Year 0" : `Year ${year}`}
                      {exitYear && year > exitYear && (
                        <div className="text-xs text-slate-500 font-normal">
                          (Post-Exit)
                        </div>
                      )}
                    </th>
                  )
                )}
              </tr>
            </thead>

            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.id}
                  className={getRowStyle(row)}
                  onMouseEnter={() => setHoveredRow(row.id)}
                  onMouseLeave={() => setHoveredRow(null)}
                >
                  <td className="px-4 py-3 border-r border-slate-200">
                    <div className="flex items-center space-x-2">
                      <span
                        className={cn(
                          row.id === "debt-eop" ? "font-bold" : "font-medium"
                        )}
                      >
                        {row.label}
                      </span>
                      <TooltipProvider>
                        <RadixTooltip>
                          <TooltipTrigger asChild>
                            <button
                              className={cn(
                                "transition-opacity",
                                hoveredRow === row.id
                                  ? "opacity-100"
                                  : "opacity-0"
                              )}
                            >
                              <Info className="h-3 w-3 text-slate-400" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="max-w-xs text-sm">
                              {getTooltipContent(row.id)}
                            </p>
                          </TooltipContent>
                        </RadixTooltip>
                      </TooltipProvider>
                    </div>
                  </td>

                  {Array.from({ length: yearsToShow }, (_, i) => i).map(
                    (year) => (
                      <td
                        key={year}
                        className="px-4 py-2 text-right border-r border-slate-100 tabular-nums"
                      >
                        <span
                          className={cn(
                            row.id === "debt-eop" ? "font-bold" : "font-medium",
                            getYearData(row, year) < 0
                              ? "text-red-600"
                              : "text-slate-900"
                          )}
                        >
                          {renderValue(getYearData(row, year))}
                        </span>
                      </td>
                    )
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="border-t border-slate-200 bg-slate-50 p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="bg-white p-3 rounded-lg border border-slate-200">
              <div className="text-slate-500 font-medium">
                Total Loan Amount
              </div>
              <div className="text-lg font-bold text-slate-900">
                {formatScaledCurrency(totalLoanAmount, currency, scale)}
              </div>
            </div>
            <div className="bg-white p-3 rounded-lg border border-slate-200">
              <div className="text-slate-500 font-medium">Total Interest</div>
              <div className="text-lg font-bold text-red-600">
                {formatScaledCurrency(Math.abs(totalInterest), currency, scale)}
              </div>
            </div>
            <div className="bg-white p-3 rounded-lg border border-slate-200">
              <div className="text-slate-500 font-medium">
                Total Principal Repaid
              </div>
              <div className="text-lg font-bold text-green-600">
                {formatScaledCurrency(totalPrincipalRepaid, currency, scale)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
